import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  ArrowRightIcon,
  BellIcon,
  Cog6ToothIcon,
  GiftIcon,
  LinkIcon,
  ServerStackIcon,
  TrophyIcon,
  UserGroupIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';
import { useLanguage } from '../../contexts/LanguageContext';
import { prospectStatusBadgeClass, prospectStatusLabel } from '../../utils/prospectStatus';
import FilterableStateDistribution from './FilterableStateDistribution';
import NextBestAction from '../ui/NextBestAction';
import { resolveAdminNextAction } from '../../utils/nextBestAction';
import EmptyState from '../ui/EmptyState';
import ClientQuickInviteCard from './ClientQuickInviteCard';
import { mapDualProgramLinks } from '../../utils/referralLinks';

function VendorRankCard({ vendor, rank }) {
  const rankStyles =
    rank === 0
      ? 'from-amber-500 to-orange-500'
      : rank === 1
        ? 'from-secondary-400 to-secondary-500'
        : rank === 2
          ? 'from-orange-600 to-red-500'
          : 'from-secondary-300 to-secondary-400';

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: rank * 0.05 }}
      className="flex items-center gap-4 rounded-2xl border border-secondary-200/80 bg-white p-4 shadow-soft  "
    >
      <div
        className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br ${rankStyles} text-sm font-bold text-white shadow-soft`}
      >
        {rank + 1}
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate font-semibold text-secondary-900 ">{vendor.username}</p>
        <div className="mt-1 flex flex-wrap gap-3 text-xs text-secondary-500 ">
          <span className="inline-flex items-center gap-1">
            <UsersIcon className="h-3.5 w-3.5 text-orange-500" />
            {vendor.clients_count}
          </span>
          <span className="inline-flex items-center gap-1">
            <LinkIcon className="h-3.5 w-3.5 text-amber-500" />
            {vendor.referrals_count}
          </span>
        </div>
      </div>
      <div className="text-right">
        <p className="text-sm font-bold tabular-nums text-success-600 ">
          ${vendor.commission?.toLocaleString?.() ?? vendor.commission}
        </p>
        <p className="text-[10px] uppercase tracking-wide text-secondary-400">USD</p>
      </div>
    </motion.div>
  );
}

function AdminHomeView({
  username,
  adminOverview,
  prospectStatuses = [],
  prospectStateData = {},
  clients = [],
  prospects = [],
  vendors = [],
  distributionLoading = false,
  pendingGifts,
  pendingGiftsLoading = false,
  hidePendingGifts = false,
  unreadNotifications = 0,
  referralLinks = [],
  showClientInvite = true,
  onOpenPendingGifts,
  onOpenSettings,
  onOpenUsers,
  onOpenNotifications,
  onOpenReferrals,
}) {
  const { t, formatMessage } = useLanguage();
  const a = t.admin;
  const h = t.enrola.adminHome;
  const sd = t.common.stateDistribution;

  const pendingGiftCount = hidePendingGifts ? 0 : (pendingGifts?.pending_admin_count ?? 0);
  const { clientsLink, partnersLink } = mapDualProgramLinks(referralLinks);

  const inviteLabels = {
    inviteEyebrow: h.inviteEyebrow,
    inviteTitle: h.inviteTitle,
    inviteSubtitle: h.inviteSubtitle,
    scanToJoin: h.scanToJoin,
    openReferrals: h.openReferrals,
    noInviteLink: h.noInviteLink,
  };

  const partnerInviteLabels = t.enrola.partnerHome;

  const nextAction = useMemo(
    () =>
      resolveAdminNextAction({
        t,
        pendingGiftCount,
        unreadNotifications,
        hidePendingGifts,
      }),
    [t, pendingGiftCount, unreadNotifications, hidePendingGifts]
  );

  const handleNextAction = (action) => {
    if (action.actionType !== 'navigate') return;
    if (action.target === 'pendingGifts') onOpenPendingGifts?.();
    if (action.target === 'notifications') onOpenNotifications?.();
    if (action.target === 'users') onOpenUsers?.();
  };

  const clientStates = adminOverview?.client_states || {};

  const topVendors = adminOverview?.top_performers?.vendors || [];
  const activeStatuses = prospectStatuses.filter((s) => s.is_active !== false);

  return (
    <div className="space-y-5 xl:space-y-6">
      <NextBestAction action={nextAction} username={username} onAction={handleNextAction} />

      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_300px] xl:grid-cols-[minmax(0,1fr)_360px] xl:gap-6">
        <div className="min-w-0 space-y-5 xl:space-y-6">
          <section className="space-y-5 min-[1600px]:grid min-[1600px]:grid-cols-2 min-[1600px]:gap-5 min-[1600px]:space-y-0">
            <FilterableStateDistribution
              variant="client"
              title={a.clientStateDistribution}
              clients={clients}
              fallbackStateData={clientStates}
              labels={{
                affiliated: a.affiliated,
                contacted: a.contacted,
                visited: a.visited,
                completed: a.completed,
              }}
              vendors={vendors}
              showVendorFilter
              filterLabels={sd}
              loading={distributionLoading}
            />
            <FilterableStateDistribution
              variant="prospect"
              title={a.prospectStateDistribution}
              prospects={prospects}
              fallbackStateData={prospectStateData}
              prospectStatuses={prospectStatuses}
              t={t}
              vendors={vendors}
              showVendorFilter
              filterLabels={sd}
              delay={0.05}
              loading={distributionLoading}
            />
          </section>

          <section>
            <div className="mb-4 flex flex-wrap items-end justify-between gap-2">
              <div className="flex items-center gap-2">
                <TrophyIcon className="h-5 w-5 text-amber-500" />
                <div>
                  <p className="micro text-secondary-500 ">
                    {h.vendorsEyebrow}
                  </p>
                  <h3 className="text-lg font-bold text-secondary-900 ">{h.vendorsTitle}</h3>
                </div>
              </div>
              {onOpenUsers ? (
                <button
                  type="button"
                  onClick={onOpenUsers}
                  className="inline-flex items-center gap-1 text-sm font-semibold text-orange-600 hover:text-orange-700 "
                >
                  {h.viewUsers}
                  <ArrowRightIcon className="h-4 w-4" />
                </button>
              ) : null}
            </div>
            <div className="space-y-3">
              {topVendors.length > 0 ? (
                topVendors.slice(0, 5).map((vendor, index) => (
                  <VendorRankCard key={vendor.id} vendor={vendor} rank={index} />
                ))
              ) : (
                <EmptyState
                  icon={UserGroupIcon}
                  title={h.vendorsEmpty}
                  className="!py-8"
                />
              )}
            </div>
          </section>
        </div>

        <aside className="space-y-4">
          <div className="space-y-4">
            {showClientInvite ? (
              <ClientQuickInviteCard
                link={clientsLink}
                secondaryLink={partnersLink}
                onOpenReferrals={onOpenReferrals}
                labels={inviteLabels}
                secondaryLabels={partnerInviteLabels}
                compact
              />
            ) : null}
          </div>

          {!hidePendingGifts ? (
            <motion.button
              type="button"
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.99 }}
              onClick={onOpenPendingGifts}
              className={`w-full rounded-2xl border p-5 text-left shadow-soft transition-colors ${
                pendingGiftCount > 0
                  ? 'border-orange-200 bg-orange-50/80  '
                  : 'border-secondary-200/80 bg-white  '
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-orange-600 ">
                    {h.pendingGiftsEyebrow}
                  </p>
                  <h3 className="mt-1 text-lg font-bold text-secondary-900 ">
                    {h.pendingGiftsTitle}
                  </h3>
                  <p className="mt-1 text-sm text-secondary-600 ">
                    {pendingGiftsLoading
                      ? '…'
                      : pendingGiftCount > 0
                        ? formatMessage(h.pendingGiftsCount, { count: pendingGiftCount })
                        : h.pendingGiftsNone}
                  </p>
                </div>
                <div className="relative flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-orange-500 to-amber-500 text-white shadow-glow">
                  <GiftIcon className="h-6 w-6" />
                  {pendingGiftCount > 0 ? (
                    <span className="absolute -right-1 -top-1 flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white ring-2 ring-white ">
                      {pendingGiftCount}
                    </span>
                  ) : null}
                </div>
              </div>
              <span className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-orange-600 ">
                {h.pendingGiftsCta}
                <ArrowRightIcon className="h-4 w-4" />
              </span>
            </motion.button>
          ) : null}

          <section className="rounded-2xl border border-orange-200/50 bg-white p-5 shadow-soft  ">
            <div className="mb-3 flex items-center gap-3">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-orange-500 to-amber-500 text-white shadow-glow">
                <ServerStackIcon className="h-6 w-6" />
              </div>
              <div>
                <h3 className="text-base font-bold text-secondary-900 ">{h.systemConfigTitle}</h3>
                <p className="text-xs text-secondary-500 ">{h.systemConfigSubtitle}</p>
              </div>
            </div>
            <p className="mb-3 text-sm text-secondary-600 ">
              {formatMessage(h.statusCount, {
                total: prospectStatuses.length,
                active: activeStatuses.length,
              })}
            </p>
            <div className="flex flex-wrap gap-2">
              {activeStatuses.slice(0, 6).map((status) => (
                <span
                  key={status.id || status.code}
                  className={`rounded-full px-2 py-1 text-[11px] font-medium ${prospectStatusBadgeClass(status.code, prospectStatuses)}`}
                >
                  {prospectStatusLabel(status.code, t, prospectStatuses)}
                </span>
              ))}
              {activeStatuses.length === 0 ? (
                <span className="text-sm text-secondary-500 ">{h.noStatuses}</span>
              ) : null}
            </div>
            {onOpenSettings ? (
              <button
                type="button"
                onClick={onOpenSettings}
                className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-orange-50 py-2.5 text-sm font-semibold text-orange-700 transition-colors hover:bg-orange-100   "
              >
                <Cog6ToothIcon className="h-4 w-4" />
                {h.configureSystem}
              </button>
            ) : null}
          </section>

          <motion.button
            type="button"
            whileHover={{ scale: 1.01 }}
            whileTap={{ scale: 0.99 }}
            onClick={onOpenNotifications}
            disabled={!onOpenNotifications}
            className={`w-full rounded-2xl border p-5 text-left shadow-soft ${
              unreadNotifications > 0
                ? 'border-amber-200 bg-amber-50/60  '
                : 'border-secondary-200/80 bg-white  '
            }`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-amber-100 text-amber-600  ">
                  <BellIcon className="h-6 w-6" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-secondary-900 ">
                    {h.notificationsTitle}
                  </h3>
                  <p className="mt-1 text-sm text-secondary-600 ">
                    {unreadNotifications > 0
                      ? formatMessage(h.unreadCount, { count: unreadNotifications })
                      : h.noUnreadNotifications}
                  </p>
                </div>
              </div>
              {unreadNotifications > 0 ? (
                <span className="rounded-full bg-amber-500 px-2.5 py-0.5 text-xs font-bold text-white">
                  {unreadNotifications}
                </span>
              ) : null}
            </div>
            <span className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-orange-600 ">
              {h.viewNotifications}
              <ArrowRightIcon className="h-4 w-4" />
            </span>
          </motion.button>
        </aside>
      </div>
    </div>
  );
}

export default AdminHomeView;
